/**
 * /auth/callback — lands here from the Cognito Hosted UI with ?code&state.
 * Exchanges the code (PKCE), stores the session, loads the caller's AppUser
 * profile via /users/me (which links the row to the Cognito sub on first
 * login), then enters the app.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { completeLogin } from '../lib/cognito.js';

export function AuthCallbackScreen() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    // React 18 StrictMode double-mounts effects; the code is single-use.
    if (ran.current) return;
    ran.current = true;

    void (async () => {
      try {
        const tokens = await completeLogin(new URLSearchParams(window.location.search));
        useAuth.getState().setToken({
          token: tokens.idToken,
          expiresIn: tokens.expiresIn,
          refreshToken: tokens.refreshToken,
        });
        const me = await api.users.me();
        useAuth.getState().setUser(me);
        void navigate({ to: '/', replace: true });
      } catch (err) {
        useAuth.getState().clear();
        const message =
          err instanceof ApiError && err.status === 403
            ? err.message // "No app account matches your login…" / deactivated
            : err instanceof Error
              ? err.message
              : 'Sign-in failed.';
        setError(message);
      }
    })();
  }, [navigate]);

  return (
    <div className="login">
      <div className="logo">
        <svg width="46" height="46" viewBox="0 0 64 64" fill="none" aria-hidden>
          <path
            d="M32 8c-9 0-16 5-16 14 0 5 1 8 3 14l5 14c1 3 3 5 5 5 3 0 4-3 5-7l1-7c0-1 1-2 2-2s2 1 2 2l1 7c1 4 2 7 5 7 2 0 4-2 5-5l5-14c2-6 3-9 3-14 0-9-7-14-16-14h-10z"
            fill="#1E5BA8"
          />
        </svg>
      </div>
      <h2>K-Dental</h2>
      <div className="tagline">Sales App</div>

      <div className="signin-card">
        {error === null ? (
          <div className="lead" style={{ marginBottom: 0 }}>
            Signing you in…
          </div>
        ) : (
          <>
            <div className="lead">{error}</div>
            <Link to="/login" className="signin-btn" style={{ display: 'block', textDecoration: 'none', boxSizing: 'border-box' }}>
              Back to sign in
            </Link>
          </>
        )}
      </div>

      <div className="footer">Bridged to Spire · Dentry platform · Gemini Logic</div>
    </div>
  );
}
